/**
 * Recruitment Type API service for ATS.
 */

import { createApiClient } from '@aaas/shared';
import type { BulkResultData, BulkResultResponse } from '../types/bulk';
import type {
  RecruitmentType,
  RecruitmentTypeListResponse,
  RecruitmentTypeResponse,
  RecruitmentTypeCreateInput,
  RecruitmentTypeUpdateInput,
} from '../types/recruitment-type';

interface ListRecruitmentTypesOptions {
  page?: number;
  size?: number;
  sortBy?: string;
  order?: 'asc' | 'desc';
  search?: string;
}

class RecruitmentTypeService {
  /**
   * List recruitment types with pagination.
   */
  async listRecruitmentTypes(
    workspaceId: string,
    idToken: string,
    options: ListRecruitmentTypesOptions = {}
  ): Promise<RecruitmentTypeListResponse> {
    const { page = 1, size = 50, sortBy = 'name', order = 'asc', search } = options;
    const skip = (page - 1) * size;

    const client = createApiClient(idToken, 'ats');
    const response = await client.get<RecruitmentTypeListResponse>(`/recruitment-types`, {
      params: { skip, limit: size, sort_by: sortBy, order, search },
      headers: { 'X-Workspace-ID': workspaceId },
    });

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to list recruitment types');
    }

    return response.data;
  }

  /**
   * Get a single recruitment type by ID.
   */
  async getRecruitmentType(
    workspaceId: string,
    recruitmentTypeId: string,
    idToken: string
  ): Promise<RecruitmentType> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.get<RecruitmentTypeResponse>(
      `/recruitment-types/${recruitmentTypeId}`,
      { headers: { 'X-Workspace-ID': workspaceId } }
    );

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to get recruitment type');
    }

    return response.data.data;
  }

  /**
   * Create a new recruitment type.
   */
  async createRecruitmentType(
    workspaceId: string,
    idToken: string,
    data: RecruitmentTypeCreateInput
  ): Promise<RecruitmentType> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.post<RecruitmentTypeResponse>(`/recruitment-types`, data, {
      headers: { 'X-Workspace-ID': workspaceId },
    });

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to create recruitment type');
    }

    return response.data.data;
  }

  /**
   * Bulk create recruitment types; duplicates skipped silently.
   */
  async bulkCreateRecruitmentTypes(
    workspaceId: string,
    idToken: string,
    names: readonly string[]
  ): Promise<BulkResultData> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.post<BulkResultResponse>(
      `/recruitment-types/bulk`,
      { names },
      { headers: { 'X-Workspace-ID': workspaceId } }
    );

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to bulk create recruitment types');
    }

    return response.data.data;
  }

  /**
   * Update a recruitment type.
   */
  async updateRecruitmentType(
    workspaceId: string,
    recruitmentTypeId: string,
    idToken: string,
    data: RecruitmentTypeUpdateInput
  ): Promise<RecruitmentType> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.patch<RecruitmentTypeResponse>(
      `/recruitment-types/${recruitmentTypeId}`,
      data,
      { headers: { 'X-Workspace-ID': workspaceId } }
    );

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to update recruitment type');
    }

    return response.data.data;
  }

  /**
   * Delete a recruitment type.
   */
  async deleteRecruitmentType(
    workspaceId: string,
    recruitmentTypeId: string,
    idToken: string
  ): Promise<void> {
    const client = createApiClient(idToken, 'ats');
    await client.delete(`/recruitment-types/${recruitmentTypeId}`, {
      headers: { 'X-Workspace-ID': workspaceId },
    });
  }
}

const recruitmentTypeService = new RecruitmentTypeService();
export default recruitmentTypeService;
